import React, { useEffect, useRef, useState } from 'react';
import Rodal from 'rodal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IApplication, IProcess } from '../types';
import { addCoordinationEmails, parseCoordinationEmails } from './coordinationEmails';

type Props = {
  show: boolean,
  submitting: boolean,
  applications: IApplication[],
  process?: IProcess,
  onClose: () => void,
  onConfirm: (coordinationEmail: string) => void,
};

const SendToCoordinationModal = ({
  show,
  submitting,
  applications,
  process,
  onClose,
  onConfirm,
}: Props) => {
  // Cada e-mail adicionado vira uma etiqueta; "draft" é o que ainda está sendo digitado.
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!show) return;

    setEmails(parseCoordinationEmails((process && process.coordination_email) || '').emails);
    setDraft('');
    setError('');
  }, [show, process]);

  // Devolve a lista resultante, ou null quando o texto tem e-mail inválido.
  const add = (text: string) => {
    if (!text.trim()) return emails;

    const result = addCoordinationEmails(emails, text);

    if (result.invalid.length > 0) {
      setError(`E-mail inválido: ${result.invalid.join(', ')}. Corrija para adicionar.`);
      return null;
    }

    setEmails(result.emails);
    setDraft('');
    setError('');

    return result.emails;
  };

  const handleChange = (value: string) => {
    // Vírgula, ponto e vírgula ou espaço (digitados ou colados) também adicionam.
    if (/[,;\s]/.test(value)) {
      if (add(value) === null) setDraft(value.replace(/[,;\s]+$/, ''));
      return;
    }

    setDraft(value);
    setError('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      add(draft);
    }

    if (event.key === 'Backspace' && draft === '' && emails.length > 0) {
      setEmails(emails.slice(0, -1));
    }
  };

  const confirm = () => {
    // O que ficou digitado sem adicionar também vale.
    const list = add(draft);

    if (list === null) return;

    if (list.length === 0) {
      return setError('Adicione ao menos um e-mail da Coordenação.');
    }

    setError('');
    onConfirm(list.join(', '));
  };

  return (
    <Rodal
      closeOnEsc
      width={620}
      height={600}
      visible={show}
      onClose={onClose}
      className="convocation-modal"
      customStyles={{ padding: 0, borderRadius: 18, overflow: 'hidden' }}
    >
      <div className="cvm-container">
        <div className="cvm-header">
          <span className="cvm-kicker">Coordenação</span>
          <h2>Direcionar documentação</h2>
        </div>
        <div className="cvm-body">
          <div className="cvm-banner">
            <FontAwesomeIcon icon="circle-info" />
            <span>
              A Coordenação receberá um e-mail com a lista abaixo e o link para consultar os
              documentos e enviar a Adaptação Curricular pelo sistema.
            </span>
          </div>
          <div className="cvm-field">
            <span>E-mails da Coordenação</span>
            <div className="cvm-emails-row">
              <div
                className="cvm-emails"
                onClick={() => inputRef.current && inputRef.current.focus()}
              >
                {emails.map(email => (
                  <span className="cvm-email-chip" key={email}>
                    {email}
                    <button
                      type="button"
                      title="Remover este e-mail"
                      aria-label={`Remover ${email}`}
                      onClick={() => setEmails(emails.filter(item => item !== email))}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  id="coordination-email"
                  ref={inputRef}
                  type="text"
                  aria-label="E-mail da Coordenação"
                  value={draft}
                  placeholder={emails.length > 0 ? 'Adicionar outro e-mail' : 'Digite o e-mail'}
                  onChange={(event) => handleChange(event.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => add(draft)}
                />
              </div>
              <button
                id="btn-add-coordination-email"
                className="btn btn-black"
                type="button"
                onClick={() => {
                  add(draft);
                  if (inputRef.current) inputRef.current.focus();
                }}
              >
                <FontAwesomeIcon icon="plus" /> Adicionar
              </button>
            </div>
            <small className="cvm-hint">
              Digite um e-mail e pressione Enter (ou clique em Adicionar). Repita para incluir
              outros. Para tirar um e-mail, clique no X dele.
            </small>
          </div>
          <p className="cvm-summary">
            <b>{applications.length} candidato(s)</b> serão direcionados.
          </p>
          <div className="cvm-candidates">
            {applications.map(application => (
              <div key={application.id} className="cvm-candidate">
                <div className="cvm-candidate-info">
                  <strong>{application.assumed_name || application.real_name}</strong>
                  <span>{application.desired_onboarding.field_step}º Período</span>
                </div>
                {application.classification != null && (
                  <span className="tcp-rank">{application.classification}º lugar</span>
                )}
              </div>
            ))}
          </div>
        </div>
        {error && <small className="cvm-error">{error}</small>}
        <div className="cvm-footer">
          <button className="btn btn-black" type="button" onClick={onClose}>Cancelar</button>
          <div className="cvm-footer-right">
            <button
              className="btn btn-primary"
              type="button"
              disabled={submitting || applications.length === 0}
              onClick={confirm}
            >
              <FontAwesomeIcon icon="paper-plane" />
              {submitting ? 'Enviando...' : 'Direcionar'}
            </button>
          </div>
        </div>
      </div>
    </Rodal>
  );
};

export default SendToCoordinationModal;
